import React, { useState } from "react";
import { NotebookPen, Link2, Quote, HelpCircle, Plus, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TYPES = [
  { key: "note", label: "Note", Icon: NotebookPen },
  { key: "link", label: "Link", Icon: Link2 },
  { key: "quote", label: "Quote", Icon: Quote },
  { key: "question", label: "Question", Icon: HelpCircle },
];

export const EntryComposer = ({ target, onClearTarget, onAdd, allowClear = true, onSuggestEntity }) => {
  const [type, setType] = useState("note");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim() && !url.trim()) return;
    setBusy(true);
    try {
      await onAdd({ entry_type: type, text: text.trim(), url: url.trim(), label: label.trim() });
      setText(""); setUrl(""); setLabel("");
    } finally { setBusy(false); }
  };

  const placeholder = {
    note: "What did you learn or want to remember?",
    link: "A short note about this link (optional)",
    quote: "Paste a passage or quote…",
    question: "What are you still unsure about?",
  }[type];

  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="entry-composer">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Adding to{" "}
          <span className="text-primary normal-case tracking-normal">
            {target ? target.title : "this path"}
          </span>
        </div>
        {target && allowClear && (
          <button onClick={onClearTarget} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="composer-clear-target">
            <X className="w-3 h-3" /> path-level instead
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {TYPES.map(({ key, label: l, Icon }) => (
          <button key={key} onClick={() => setType(key)} data-testid={`entry-type-${key}`}
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
              type === key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder}
        className="min-h-[72px] text-sm" data-testid="entry-text" />

      {(type === "link" || url) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="h-9 text-sm" data-testid="entry-url" />
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Link label (optional)" className="h-9 text-sm" data-testid="entry-label" />
        </div>
      )}
      {type !== "link" && !url && (
        <button onClick={() => setUrl(" ")} className="text-[11px] text-primary hover:underline mt-2 inline-flex items-center gap-1" data-testid="entry-add-link-toggle">
          <Link2 className="w-3 h-3" /> attach a link
        </button>
      )}

      <div className="flex items-center justify-between gap-2 mt-3">
        {onSuggestEntity ? (
          <button type="button" onClick={() => onSuggestEntity({ text, url, label })}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="composer-suggest-entity">
            <Sparkles className="w-3.5 h-3.5" /> This is a new entity — suggest it
          </button>
        ) : <span />}
        <Button size="sm" onClick={submit} disabled={busy || (!text.trim() && !url.trim())}
          className="bg-primary hover:bg-primary/90" data-testid="entry-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add entry
        </Button>
      </div>
    </div>
  );
};
